// Lightweight chart primitives (custom SVG — no libs)
import React from 'react'


const fmtCurEUR = (v) => '€\u00a0' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCurShort = (v) => {
  if (v >= 1e6) return '€\u00a0' + (v/1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '€\u00a0' + Math.round(v/1e3) + 'k';
  return '€\u00a0' + v.toFixed(0);
};

// Sparkline for KPI cards
const Sparkline = ({ data, color = 'var(--ht-gold-500)', height = 36, fill = true }) => {
  const w = 200, h = height, pad = 2;
  const max = Math.max(...data), min = Math.min(...data);
  const norm = v => h - pad - ((v - min) / (max - min || 1)) * (h - pad*2);
  const step = (w - pad*2) / (data.length - 1);
  const points = data.map((v, i) => [pad + i * step, norm(v)]);
  const path = points.map((p,i) => (i?'L':'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const areaPath = path + ` L ${(w-pad).toFixed(1)},${h-pad} L ${pad.toFixed(1)},${h-pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={height} preserveAspectRatio="none">
      {fill && <path d={areaPath} fill={color} opacity="0.12"/>}
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={points[points.length-1][0]} cy={points[points.length-1][1]} r="2.4" fill={color}/>
    </svg>
  );
};

// Area chart for revenue trend
const RevenueChart = ({ series }) => {
  const w = 720, h = 220, padL = 56, padR = 16, padT = 12, padB = 28;
  const data = series.map(s => s.amt);
  const max = Math.max(...data) * 1.08;
  const min = 0;
  const xStep = (w - padL - padR) / (data.length - 1);
  const norm = v => padT + (1 - (v - min) / (max - min)) * (h - padT - padB);
  const points = data.map((v, i) => [padL + i * xStep, norm(v)]);
  const path = points.map((p,i) => (i?'L':'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const areaPath = path + ` L ${(w-padR).toFixed(1)},${h-padB} L ${padL.toFixed(1)},${h-padB} Z`;

  // gridlines + y-axis labels (5 tiers)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => {
    const v = min + (max - min) * t;
    const y = norm(v);
    return { v, y };
  });

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} style={{display:'block'}}>
      <defs>
        <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f5a800" stopOpacity="0.22"/>
          <stop offset="100%" stopColor="#f5a800" stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {/* gridlines */}
      {ticks.map((t,i) => (
        <g key={i}>
          <line x1={padL} x2={w-padR} y1={t.y} y2={t.y} stroke="#e4e4d8" strokeDasharray={i===ticks.length-1?'':'2 3'}/>
          <text x={padL-8} y={t.y+3} textAnchor="end" fontSize="9.5" fill="#8a8d7a" fontFamily="JetBrains Mono">
            {fmtCurShort(t.v)}
          </text>
        </g>
      ))}
      {/* area + line */}
      <path d={areaPath} fill="url(#rev-grad)"/>
      <path d={path} stroke="#5a5e4b" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      {/* points */}
      {points.map((p,i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i===points.length-1?3.5:2}
          fill={i===points.length-1?'#f5a800':'#5a5e4b'}
          stroke="#fff" strokeWidth={i===points.length-1?2:1}/>
      ))}
      {/* x-axis labels (every 2nd) */}
      {series.map((s,i) => i%2===0 ? (
        <text key={i} x={padL + i*xStep} y={h-10} textAnchor="middle" fontSize="9.5" fill="#8a8d7a" fontFamily="JetBrains Mono">{s.d.slice(4)}</text>
      ) : null)}
      {/* highlight last */}
      <line x1={points[points.length-1][0]} x2={points[points.length-1][0]} y1={padT} y2={h-padB} stroke="#f5a800" strokeDasharray="2 3" opacity="0.6"/>
    </svg>
  );
};

// Horizontal stacked bar chart for partner balances
const PartnerBalances = ({ data }) => {
  const max = Math.max(...data.map(d => d.incoming + d.outgoing));
  return (
    <div style={{padding: '4px 0'}}>
      {data.map((d,i) => {
        const inPct  = (d.incoming / max) * 100;
        const outPct = (d.outgoing / max) * 100;
        return (
          <div key={i} style={{padding:'10px 0', borderTop: i? '1px solid var(--border-soft)':'none'}}>
            <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
              <span className="rics">{d.rics}</span>
              <span style={{fontWeight:500, fontSize:13}}>{d.name}</span>
              <span style={{marginLeft:'auto', fontFamily:'var(--font-mono)', fontSize:12, fontWeight:600}}>
                {fmtCurShort(d.incoming - d.outgoing)} <span style={{color:'var(--text-dim)', fontWeight:400, fontSize:10.5}}>net</span>
              </span>
            </div>
            <div style={{height:8, display:'flex', borderRadius:4, overflow:'hidden', background:'var(--surface-2)'}}>
              <div style={{width: inPct+'%', background:'var(--ht-olive-500)'}} title={'Incoming ' + fmtCurEUR(d.incoming)}/>
              <div style={{width: outPct+'%', background:'var(--ht-gold-500)'}} title={'Outgoing ' + fmtCurEUR(d.outgoing)}/>
            </div>
            <div style={{display:'flex', gap:14, marginTop:4, fontSize:10.5, fontFamily:'var(--font-mono)', color:'var(--text-dim)'}}>
              <span><span style={{display:'inline-block', width:6, height:6, background:'var(--ht-olive-500)', borderRadius:2, marginRight:4}}/>in {fmtCurShort(d.incoming)}</span>
              <span><span style={{display:'inline-block', width:6, height:6, background:'var(--ht-gold-500)', borderRadius:2, marginRight:4}}/>out {fmtCurShort(d.outgoing)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Donut for status breakdown
const StatusDonut = ({ data }) => {
  const total = data.reduce((s,d) => s+d.v, 0);
  const r = 42, cx=60, cy=60;
  const C = 2*Math.PI*r;
  let off = 0;
  return (
    <div style={{display:'flex', alignItems:'center', gap:18}}>
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--surface-2)" strokeWidth="14"/>
        {data.map((d,i) => {
          const len = (d.v/total) * C;
          const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={d.color} strokeWidth="14"
            strokeDasharray={`${len} ${C-len}`}
            strokeDashoffset={-off}
            transform={`rotate(-90 ${cx} ${cy})`}/>;
          off += len;
          return el;
        })}
        <text x={cx} y={cy-2} textAnchor="middle" fontSize="20" fontWeight="700" fontFamily="JetBrains Mono" fill="var(--text)">{total.toLocaleString()}</text>
        <text x={cx} y={cy+14} textAnchor="middle" fontSize="9" fill="var(--text-dim)" letterSpacing="0.06em">TRANSACTIONS</text>
      </svg>
      <div style={{display:'flex', flexDirection:'column', gap:7, fontSize:12, flex:1}}>
        {data.map((d,i) => (
          <div key={i} style={{display:'flex', alignItems:'center', gap:8}}>
            <span style={{width:9, height:9, borderRadius:2, background:d.color}}/>
            <span style={{color:'var(--text)'}}>{d.label}</span>
            <span style={{marginLeft:'auto', fontFamily:'var(--font-mono)', fontWeight:600}}>{d.v.toLocaleString()}</span>
            <span style={{fontFamily:'var(--font-mono)', color:'var(--text-dim)', fontSize:10.5, minWidth:36, textAlign:'right'}}>{((d.v/total)*100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

window.fmtCurEUR = fmtCurEUR;
window.fmtCurShort = fmtCurShort;
window.Sparkline = Sparkline;
window.RevenueChart = RevenueChart;
window.PartnerBalances = PartnerBalances;
window.StatusDonut = StatusDonut;

export { Sparkline, RevenueChart, PartnerBalances, StatusDonut, fmtCurEUR, fmtCurShort }
